import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    getSuppliers,
    createSupplier,
    updateSupplier,
    getSupplierInvoices,
    createSupplierInvoice,
    updateSupplierInvoice,
    deleteSupplierInvoice,
    uploadSupplierInvoiceFile,
    extractSupplierInvoiceFields,
    getSupplierInvoiceFileBlob,
    getOutstandingSupplierInvoices,
    getProjects,
    getFxPreview,
    confirmInboundDocument,
    getInboundDocumentFileBlob,
} from '../../lib/api';
import { formatSEK, formatDate, formatNumber } from '../../lib/format-utils';
import { getApiErrorMessage } from '../../lib/errors';
import { cn } from '../../lib/utils';
// The rule for what an attached document may write onto this dialog, kept
// out of this file on purpose — it is the sole guard on Principle 2 here and
// is pinned by supplier-prefill.test.js. Change it there first.
import {
    CURRENCIES,
    INVOICE_PREFILL_KEYS,
    buildPrefillPatch,
} from './supplier-prefill';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge, badgeVariants } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '../../components/ui/command';
import SupplierInvoicePreview from '../../components/supplier-invoice-preview';
import { toast } from 'sonner';
import {
    Plus,
    Pencil,
    Trash2,
    ChevronDown,
    Clock,
    DollarSign,
    ExternalLink,
    Loader2,
    FileText,
    Paperclip,
    StickyNote,
    Users,
    Check,
    ChevronsUpDown,
    Building2,
    AlertTriangle,
} from 'lucide-react';
import MonthNavigator from '../../components/month-navigator';
import { SummaryCard, SummaryCardRow } from '../../components/summary-cards';

// Phase 22 (PRD 11.4): the Betalsätt (payment method) CONTROL was removed from
// the invoice form — the finance manager pays a whole month's invoices in one
// bank run, so per-invoice method was noise. The field itself is untouched in
// the schema and the API (Principle 4, additive only): this form simply never
// sends it, so an already-stored payment_method survives every edit.

// shadcn Select cannot hold an empty-string item value — mirrors the
// NO_COST_CODE sentinel pattern in ProjectsPage.jsx. Mapped back to '' on save.
// Used by the Manage Suppliers "default project" control.
const SELECT_NONE = '__none__';

// Accepted MIME types — must match backend _ALLOWED_MIME_TYPES in
// routes/supplier_invoices.py (pdf/png/jpeg/webp — no HEIC for this surface)
const FILE_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB cap (spec)

const RIKSBANK_URL = 'https://www.riksbank.se/en-gb/statistics/interest-rates-and-exchange-rates/search-interest-and-exchange-rates/';

// Formats a Date using LOCAL calendar components (not toISOString(), which
// converts to UTC and can roll into the wrong day depending on the browser's
// timezone offset — e.g. shortly after local midnight in a UTC+ timezone).
function toLocalISODate(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// Parses a "YYYY-MM-DD" string into a local Date. Avoids `new Date(dateStr)`,
// which the spec treats as UTC midnight for date-only strings and can shift
// into the adjacent local day depending on timezone offset.
function parseISODateLocal(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function defaultInvoiceDate(year, month) {
    const today = new Date();
    if (today.getFullYear() === year && today.getMonth() + 1 === month) {
        return toLocalISODate(today);
    }
    return `${year}-${String(month).padStart(2, '0')}-01`;
}

// An invoice reads as overdue only while it is still unpaid AND its due date
// has strictly passed — a paid invoice, or one with no due date, is never
// overdue. `todayIso` must be a "YYYY-MM-DD" string (toLocalISODate output)
// so the comparison stays a plain string compare, never new Date(str).
function isOverdueInvoice(inv, todayIso) {
    return inv.payment_status === 'unpaid' && !!inv.due_date && inv.due_date < todayIso;
}

// Badge for the three payment statuses (paid / scheduled = upplagd för
// betalning / unpaid); an overdue unpaid invoice gets the destructive variant.
function PaymentStatusBadge({ inv, overdue, t }) {
    const status = inv.payment_status || 'paid';
    if (status === 'unpaid') {
        return (
            <Badge
                variant={overdue ? 'destructive' : 'outline'}
                className={overdue ? '' : 'bg-amber-100 text-amber-800 border-amber-300'}
            >
                {overdue ? t('supplier_invoices.status_overdue') : t('supplier_invoices.status_unpaid')}
            </Badge>
        );
    }
    if (status === 'scheduled') {
        return (
            <Badge variant="outline" className="bg-sky-100 text-sky-800 border-sky-300">
                {t('supplier_invoices.status_scheduled')}
            </Badge>
        );
    }
    return <Badge variant="secondary">{t('supplier_invoices.status_paid')}</Badge>;
}

function emptyInvoiceForm(year, month) {
    return {
        supplier_id: '',
        supplier_name: '',
        invoice_number: '',
        invoice_date: defaultInvoiceDate(year, month),
        due_date: '',
        // Phase 22 (PRD 11.1): the register is an INBOX, not a log of settled
        // bills. Invoices arrive continuously and are paid in one monthly run
        // on their förfallodatum, so a new invoice is UNPAID with no paid date
        // until that run happens. Paying it is a later, separate act.
        payment_status: 'unpaid',
        paid_date: '',
        payment_reference: '',
        amount: '',
        // Moms — the VAT component OF `amount`, never on top of it. Empty
        // string means "not recorded" and is sent as null, never 0.
        vat_amount: '',
        currency: 'SEK',
        project_id: '',
        project_name: '',
        // Cost center is now DERIVED from the project — kept only for
        // read-only display. For a legacy invoice (project_id null) this
        // holds the invoice's own stored cost center; see openEditInvoiceDialog.
        cost_code_id: '',
        cost_code_name: '',
        // The Fortnox kostnadsställe CODE (never the id, never the project's
        // own code) — the only thing the read-only field itself may show.
        cost_code_code: '',
        // Phase 23 (PRD 12.1/12.2): which of project_id's configured
        // activities this invoice is attributed to — required only when the
        // picked project defines any (utils/activities.py's resolve_activity,
        // shared with day-receipt-form.jsx and the other entry surfaces).
        activity_id: '',
        activity_name: '',
        note: '',
        file_path: null,
        file_name: null,
    };
}

function emptySupplierForm() {
    return { name: '', org_number: '', default_project_id: '', notes: '' };
}

export default function SupplierInvoicesPage() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // ── Period ──────────────────────────────────────────────────────────────
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    // ── Data ────────────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    // An invoice's cost center is now derived from its project — the project
    // list is what both the invoice dialog and the Manage Suppliers "default
    // project" control read from.
    const [projects, setProjects] = useState([]);

    // ── Outstanding (cross-month unpaid) panel ──────────────────────────────
    const [outstanding, setOutstanding] = useState([]);
    const [outstandingOpen, setOutstandingOpen] = useState(false);
    // Set once, on the first successful outstanding fetch, so the panel's
    // open/closed state doesn't jump around on every subsequent refetch.
    const outstandingDefaultSetRef = useRef(false);

    // ── Payment status filter — client-side over the loaded month ──────────
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const statusFilter = useMemo(() => {
        const param = (searchParams.get('status') || searchParams.get('filter') || '').toLowerCase();
        return ['all', 'unpaid', 'scheduled', 'paid'].includes(param) ? param : 'all';
    }, [searchParams]);

    const handleStatusFilterChange = (newStatus) => {
        const params = new URLSearchParams(location.search);
        if (newStatus === 'all') {
            params.delete('status');
            params.delete('filter');
        } else {
            params.set('status', newStatus);
            params.delete('filter');
        }
        const newSearch = params.toString() ? `?${params.toString()}` : '';
        navigate(`${location.pathname}${newSearch}`, { replace: true, state: location.state });
    };

    // ── Mark-as-paid (row action, month list + Outstanding panel) ──────────
    const [markingPaidId, setMarkingPaidId] = useState(null);

    // ── Invoice add/edit dialog ─────────────────────────────────────────────
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [invoiceForm, setInvoiceForm] = useState(() => emptyInvoiceForm(year, month));
    // Renamed from costCodeTouched (round 1) — the project is now the thing a
    // supplier-default can prefill, and the thing "don't override what the
    // user already touched" applies to.
    const [projectTouched, setProjectTouched] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [savingInvoice, setSavingInvoice] = useState(false);
    // ── Document prefill (Phase 25.2) ───────────────────────────────────────
    // What the attached document said. It is NOT a record of what was filled:
    // the "from document" marker compares this against the live form value, so
    // a field the user then edits stops matching and loses its marker with no
    // per-field bookkeeping anywhere.
    const [extractedInvoice, setExtractedInvoice] = useState(null);
    const [extractingInvoice, setExtractingInvoice] = useState(false);
    // The values the invoice dialog OPENED with, or null when editing an
    // existing document — see buildPrefillPatch.
    const pristineInvoiceRef = useRef(null);
    // The file whose extraction is currently wanted. Picking a second file
    // while the first is still in flight must not let the stale answer land.
    const invoiceExtractRef = useRef(null);
    const [fxPreview, setFxPreview] = useState(null);
    const [viewingFile, setViewingFile] = useState(false);
    // Below tablet width the document preview would squeeze the form into
    // unusability, so there it is collapsed behind a toggle — the form is the
    // primary surface. From lg up the two panes sit side by side and this
    // flag is irrelevant.
    const [previewOpenMobile, setPreviewOpenMobile] = useState(false);

    // ── Supplier combobox (inside the invoice dialog) ──────────────────────
    const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
    const [supplierQuery, setSupplierQuery] = useState('');
    const [creatingSupplier, setCreatingSupplier] = useState(false);
    // Quick-create from the invoice's supplier picker: name + org number, so a
    // new supplier can be matched by org number on the next invoice.
    const [quickSupplier, setQuickSupplier] = useState(null);
    // "+ Add new supplier" needs a name to create — with none typed yet, it
    // focuses this instead of falling back to a native window.prompt.

    // ── Project combobox (inside the invoice dialog) — no inline "create
    // new project" affordance; projects are created on the Projects page. ──
    const [projectPickerOpen, setProjectPickerOpen] = useState(false);
    const [projectQuery, setProjectQuery] = useState('');

    // ── Delete invoice confirmation ─────────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // ── Manage suppliers dialog ─────────────────────────────────────────────
    const [manageSuppliersOpen, setManageSuppliersOpen] = useState(false);
    const [supplierForm, setSupplierForm] = useState(emptySupplierForm());
    const [editingSupplierId, setEditingSupplierId] = useState(null);
    const [savingSupplier, setSavingSupplier] = useState(false);

    // ── Fetch ────────────────────────────────────────────────────────────────
    // requestIdRef guards against out-of-order responses: if the admin flips
    // through months quickly, an older in-flight request must never overwrite
    // the list with stale data after a newer request has already resolved.
    const requestIdRef = useRef(0);
    const fetchAll = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const [invRes, supRes, projRes, outRes] = await Promise.all([
                getSupplierInvoices(year, month),
                getSuppliers(),
                // {all:true} bypasses the membership filter GET /projects applies
                // to a plain admin caller — suppliers/invoices are company-wide,
                // so every project must be pickable here regardless of who is on it.
                getProjects({ all: true }).catch(() => ({ data: [] })),
                // Outstanding is cross-month and independent of the browsed
                // month — a failure here shouldn't blank out the month list.
                getOutstandingSupplierInvoices().catch(() => ({ data: [] })),
            ]);
            if (requestId !== requestIdRef.current) return;
            setInvoices(invRes.data || []);
            setSuppliers(supRes.data || []);
            setProjects(projRes.data || []);
            const outData = outRes.data || [];
            setOutstanding(outData);
            if (!outstandingDefaultSetRef.current) {
                outstandingDefaultSetRef.current = true;
                const todayIso = toLocalISODate(new Date());
                setOutstandingOpen(outData.some((inv) => isOverdueInvoice(inv, todayIso)));
            }
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            setInvoices([]);
            toast.error(t('supplier_invoices.failed_load'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [year, month, t]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (month === 1) {
                setMonth(12);
                setYear(year - 1);
            } else {
                setMonth(month - 1);
            }
        } else {
            if (month === 12) {
                setMonth(1);
                setYear(year + 1);
            } else {
                setMonth(month + 1);
            }
        }
    };

    // ── Derived ──────────────────────────────────────────────────────────────
    const activeSuppliers = useMemo(
        () => suppliers.filter((s) => s.is_active !== false),
        [suppliers]
    );

    // Projects selectable in the invoice dialog: active ones, plus the
    // project already on the invoice being edited even if it's since gone
    // inactive — mirrors the rule the old cost-code select used.
    const selectableProjects = useMemo(
        () => projects.filter((p) => p.active || p.id === invoiceForm.project_id),
        [projects, invoiceForm.project_id]
    );

    // Same rule for the Manage Suppliers "default project" control.
    const selectableProjectsForSupplierDefault = useMemo(
        () => projects.filter((p) => p.active || p.id === supplierForm.default_project_id),
        [projects, supplierForm.default_project_id]
    );

    // The project currently picked in the invoice dialog — used to derive its
    // cost center's code and name, and to show its activities (if any).
    const selectedInvoiceProject = useMemo(
        () => projects.find((p) => p.id === invoiceForm.project_id) || null,
        [projects, invoiceForm.project_id]
    );

    // Recomputed per render — cheap, and keeps "overdue" correct across a
    // midnight rollover without needing its own effect/timer.
    const todayISO = toLocalISODate(new Date());

    // Status filter (All / Unpaid / Paid) applied client-side over the
    // already-loaded month — the backend deliberately has no such param.
    const filteredInvoices = useMemo(() => {
        return invoices.filter((inv) => {
            if (statusFilter !== 'all' && (inv.payment_status || 'paid') !== statusFilter) return false;
            return true;
        });
    }, [invoices, statusFilter]);

    // Cost-center grouping and subtotals follow whatever is filtered into view.
    const groups = useMemo(() => {
        const map = new Map();
        for (const inv of filteredInvoices) {
            const key = inv.cost_code_id || '__none__';
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    name: inv.cost_code_name || t('supplier_invoices.no_cost_center'),
                    items: [],
                    subtotal: 0,
                });
            }
            const g = map.get(key);
            g.items.push(inv);
            g.subtotal += Number(inv.amount_sek ?? inv.amount ?? 0);
        }
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [filteredInvoices, t]);

    // GRAND TOTAL always means the sum of ALL of the month's invoices, paid
    // and unpaid — the status filter must never change what this number means.
    const grandTotal = useMemo(
        () => invoices.reduce((sum, inv) => sum + Number(inv.amount_sek ?? inv.amount ?? 0), 0),
        [invoices]
    );

    // Subtotal of just this month's unpaid invoices — shown alongside GRAND
    // TOTAL (not instead of it), independent of the status filter.
    const unpaidTotal = useMemo(
        () =>
            invoices
                .filter((inv) => (inv.payment_status || 'paid') === 'unpaid')
                .reduce((sum, inv) => sum + Number(inv.amount_sek ?? inv.amount ?? 0), 0),
        [invoices]
    );

    // Overdue count for the BROWSED MONTH only. Deliberately NOT
    // `outstandingOverdueCount` below, which counts overdue invoices across
    // every month: the summary cards sit directly under the month header and
    // every other figure in that row (count, unpaid, grand total) is scoped to
    // the month, so borrowing a cross-month number into the row would put two
    // different meanings of "overdue" on one screen. The cross-month figure
    // keeps its home in the Outstanding panel, which says out loud that it is
    // cross-month.
    const monthOverdueCount = useMemo(
        () => invoices.filter((inv) => isOverdueInvoice(inv, todayISO)).length,
        [invoices, todayISO]
    );

    const outstandingTotal = useMemo(
        () => outstanding.reduce((sum, inv) => sum + Number(inv.amount_sek ?? inv.amount ?? 0), 0),
        [outstanding]
    );
    const outstandingOverdueCount = useMemo(
        () => outstanding.filter((inv) => isOverdueInvoice(inv, todayISO)).length,
        [outstanding, todayISO]
    );

    // ── Invoice dialog handlers ──────────────────────────────────────────────
    const openCreateInvoiceDialog = () => {
        setEditingInvoice(null);
        const blank = emptyInvoiceForm(year, month);
        setInvoiceForm(blank);
        // Remember what "untouched" looks like, so a document may replace this
        // month's default date and the default SEK — but nothing the user then
        // changes himself.
        pristineInvoiceRef.current = blank;
        setExtractedInvoice(null);
        setProjectTouched(false);
        setPendingFile(null);
        setFxPreview(null);
        setSupplierQuery('');
        setProjectQuery('');
        setPreviewOpenMobile(false);
        setInvoiceDialogOpen(true);
    };

    const openEditInvoiceDialog = (inv) => {
        setEditingInvoice(inv);
        setInvoiceForm({
            supplier_id: inv.supplier_id,
            supplier_name: inv.supplier_name,
            invoice_number: inv.invoice_number || '',
            invoice_date: inv.invoice_date,
            due_date: inv.due_date || '',
            // Legacy invoices read back as "paid" from the backend already —
            // guard with the same fallback here in case of a stale cache.
            payment_status: inv.payment_status || 'paid',
            paid_date: inv.paid_date || '',
            payment_reference: inv.payment_reference || '',
            amount: String(inv.amount ?? ''),
            // null (never recorded) reads back as an empty field, not "0" —
            // "no VAT recorded" and "VAT is zero" are different facts.
            vat_amount: inv.vat_amount === null || inv.vat_amount === undefined ? '' : String(inv.vat_amount),
            currency: inv.currency || 'SEK',
            project_id: inv.project_id || '',
            project_name: inv.project_name || '',
            // Legacy invoices (project_id null) keep their own stored cost
            // center here — shown read-only, never forced onto a project.
            cost_code_id: inv.cost_code_id || '',
            cost_code_name: inv.cost_code_name || '',
            cost_code_code: inv.cost_code_code || '',
            activity_id: inv.activity_id || '',
            activity_name: inv.activity_name || '',
            note: inv.note || '',
            file_path: inv.file_path || null,
            file_name: inv.file_name || null,
        });
        // Project (if any) already has a value — never let a supplier
        // default-project prefill override it.
        setProjectTouched(true);
        // Editing a stored document: every value on this form is real data,
        // never a default, so a replacement file may only fill fields that are
        // genuinely empty.
        pristineInvoiceRef.current = null;
        setExtractedInvoice(null);
        setPendingFile(null);
        setFxPreview(null);
        setSupplierQuery('');
        setProjectQuery('');
        setPreviewOpenMobile(false);
        setInvoiceDialogOpen(true);
    };

    const handleSelectSupplier = useCallback((supplier) => {
        setInvoiceForm((prev) => {
            const base = { ...prev, supplier_id: supplier.id, supplier_name: supplier.name };
            if (projectTouched || !supplier.default_project_id) return base;
            // Prefill the supplier's default project — same "don't override
            // what the user already touched" semantics the old cost-code
            // prefill had. A default project that's gone (deleted) or is
            // itself blocked (no cost center) is skipped rather than
            // prefilling a broken state.
            const project = projects.find((p) => p.id === supplier.default_project_id);
            if (!project || !project.cost_code_id) return base;
            return {
                ...base,
                project_id: project.id,
                project_name: project.name,
                cost_code_id: project.cost_code_id,
                cost_code_name: project.cost_code_name || '',
                cost_code_code: project.cost_code_code || '',
                // The prefilled project may define different activities (or
                // none) than whatever was picked before — never carry a
                // now-unrelated activity_id across a project change.
                activity_id: '',
                activity_name: '',
            };
        });
        setSupplierPickerOpen(false);
        setSupplierQuery('');
    }, [projectTouched, projects]);

    const openQuickSupplier = () => {
        setQuickSupplier({
            name: supplierQuery.trim(),
            org_number: extractedInvoice?.org_number || '',
        });
        setSupplierPickerOpen(false);
    };

    const handleCreateSupplierInline = async () => {
        const name = (quickSupplier?.name || '').trim();
        if (!name) {
            toast.error(t('supplier_invoices.supplier_name_required'));
            return;
        }
        setCreatingSupplier(true);
        try {
            const res = await createSupplier({ name, org_number: quickSupplier.org_number.trim() || null });
            setSuppliers((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            handleSelectSupplier(res.data);
            setQuickSupplier(null);
            toast.success(t('supplier_invoices.supplier_created', { name }));
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_create_supplier')));
        } finally {
            setCreatingSupplier(false);
        }
    };

    // Picking a project derives the cost center (shown read-only below the
    // picker) — moving a project's cost center is expected and fine.
    const handleSelectProject = useCallback((project) => {
        setProjectTouched(true);
        setInvoiceForm((prev) => ({
            ...prev,
            project_id: project.id,
            project_name: project.name,
            cost_code_id: project.cost_code_id || '',
            cost_code_name: project.cost_code_name || '',
            cost_code_code: project.cost_code_code || '',
            // A different project may define different activities (or none
            // at all) — an activity picked against the old project can never
            // be valid against this one, so it is not carried over.
            activity_id: '',
            activity_name: '',
        }));
        setProjectPickerOpen(false);
        setProjectQuery('');
    }, []);

    // ── Intake hand-off (Phase 25.4, PRD 13.7) ──────────────────────────────
    // A held document confirmed on the intake screen arrives here as router
    // state and opens THESE dialogs — the real ones, with their validation,
    // their project picker and their FX conversion. The intake screen
    // deliberately has no form of its own: a second one would drift from this
    // one from the first edit onwards.
    //
    // What it holds while the dialog is open: the HELD DOCUMENT's id, so the
    // row can be marked consumed after the invoice is safely created, and the
    // fact that the attached file is not yet reachable through the
    // supplier-invoice file endpoint (no supplier_invoices row references it
    // until the save succeeds — that is the whole point of the holding area).
    const [intake, setIntake] = useState(null);
    // Which hand-off has already been consumed, so a re-render or a back
    // navigation cannot reopen the dialog for a document already dealt with.
    const intakeHandledRef = useRef(null);

    useEffect(() => {
        const handoff = location.state?.intake;
        if (!handoff?.id || intakeHandledRef.current === handoff.id) return;
        // Suppliers and projects must be loaded first: the matched supplier
        // has to resolve to a NAME, and its default project is the one choice
        // a mail genuinely cannot make that we can still make for the user.
        if (loading) return;

        intakeHandledRef.current = handoff.id;
        // Drop the router state immediately. Without this a browser refresh
        // would reopen the dialog for a document that was already confirmed,
        // and offer to create the same invoice twice.
        navigate(location.pathname, { replace: true, state: null });
        setIntake(handoff);

        const supplier = handoff.supplierId
            ? suppliers.find((s) => s.id === handoff.supplierId)
            : null;
        // The matched supplier is as much "read off the document" as the amount
        // is — it came from a Luhn-verified organisationsnummer — so it is
        // folded in here and earns the same "from the document" marker beside
        // the field. buildPrefillPatch never looks at it: supplier_id is not
        // in INVOICE_PREFILL_KEYS.
        const fields = {
            ...(handoff.fields || {}),
            ...(handoff.supplierId ? { supplier_id: handoff.supplierId } : {}),
        };

        openCreateInvoiceDialog();
        setExtractedInvoice(fields);
        setInvoiceForm((prev) => ({
            ...prev,
            ...buildPrefillPatch(prev, fields, INVOICE_PREFILL_KEYS, pristineInvoiceRef.current),
            file_path: handoff.filePath,
            file_name: handoff.fileName,
        }));
        if (supplier) handleSelectSupplier(supplier);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, loading, suppliers]);

    /**
     * Marks the held document consumed AFTER the supplier invoice is safely
     * created — the second of the two calls, never the first.
     *
     * If this one fails the invoice still exists and the held row stays
     * pending, so the intake screen offers the same document again. That is
     * the safe direction (Principle 2: an extra visible draft beats an
     * invisible loss) but it is NOT silent: the user is told there is now a
     * duplicate waiting, because a duplicate nobody knows about is how one
     * gets paid twice.
     */
    const recordIntakeConfirmation = async (invoiceId) => {
        if (!intake?.id) return;
        try {
            await confirmInboundDocument(intake.id, invoiceId);
        } catch {
            toast.error(t('supplier_invoices.intake_confirm_failed'), {
                description: t('supplier_invoices.intake_confirm_failed_hint'),
                duration: 10000,
            });
        } finally {
            setIntake(null);
        }
    };

    // Keeps the payment fields COHERENT in both directions (finding 5.137):
    //   → "paid"   fills a paid date if there isn't one, defaulting to the
    //              förfallodatum (the day the bank payment is actually booked)
    //              and falling back to today when the invoice has no due date.
    //              Without this an invoice could be marked paid with no date.
    //   → "unpaid"/"scheduled" clears the paid date, mirroring the server
    //              invariant that only a paid invoice carries one.
    // The admin can still overwrite the filled-in date; this only decides the
    // starting value so the field is never left silently empty.
    const handlePaymentStatusChange = (value) => {
        setInvoiceForm((prev) => {
            if (value !== 'paid') {
                return { ...prev, payment_status: value, paid_date: '' };
            }
            return {
                ...prev,
                payment_status: 'paid',
                paid_date: prev.paid_date || prev.due_date || toLocalISODate(new Date()),
            };
        });
    };

    // Reads a picked file's fields without storing it (Phase 25.2, PRD 13.3).
    //
    // NEVER blocks the dialog and NEVER toasts. Extraction is an assist: if the
    // call fails, times out or finds nothing, the form stays exactly as usable
    // as it was before this feature existed. "Nothing found" in particular is
    // an ordinary outcome of a deliberately lossy extractor, not a failure, and
    // an error toast for it would train the user to ignore toasts.
    const readDocumentFields = useCallback(async (file, requestRef, setExtracting) => {
        // Images are not sent at all — OCR is out of scope for this phase, so
        // the server would read up to 10 MB only to answer {}.
        // Claim the slot BEFORE the image early-return, so an in-flight PDF
        // read cannot land on a form whose document is now an image.
        requestRef.current = file;
        if (file.type !== 'application/pdf') {
            setExtracting(false);
            return null;
        }
        setExtracting(true);
        try {
            const res = await extractSupplierInvoiceFields(file);
            // A second file picked meanwhile owns the form now; this answer is
            // about a document the user has already replaced.
            return requestRef.current === file ? (res.data?.extracted || null) : null;
        } catch {
            return null;
        } finally {
            if (requestRef.current === file) setExtracting(false);
        }
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0] || null;
        if (!file) return;
        if (file.size > MAX_FILE_BYTES) {
            toast.error(t('supplier_invoices.file_too_large'));
            e.target.value = '';
            return;
        }
        setPendingFile(file);
        readDocumentFields(file, invoiceExtractRef, setExtractingInvoice).then((extracted) => {
            if (!extracted) return;
            // The document's supplier, when it named one this tenant knows.
            // `supplier_id` comes back only on a Luhn-verified organisations-
            // nummer match scoped to this company, so it is verified rather
            // than guessed; an id the loaded list does not hold is skipped
            // rather than shown as a blank picker.
            const matched = extracted.supplier_id
                ? suppliers.find((s) => s.id === extracted.supplier_id)
                : null;
            setExtractedInvoice(extracted);
            setInvoiceForm((prev) => ({
                ...prev,
                ...buildPrefillPatch(prev, extracted, INVOICE_PREFILL_KEYS, pristineInvoiceRef.current),
                // Blank-only here too: a supplier the user already chose is
                // never replaced by the one the document names.
                ...(matched && !prev.supplier_id
                    ? { supplier_id: matched.id, supplier_name: matched.name }
                    : {}),
            }));
        });
    };

    // `fetchBlob` is the endpoint that can PROVE this tenant owns the path.
    // While an intake hand-off is open the file belongs to a held document and
    // no supplier_invoices row references it yet, so the supplier-invoice file
    // route legitimately 404s on it — the intake route is the one that owns it
    // until the invoice is saved.
    const handleViewFile = async (filePath, fileName, fetchBlob = getSupplierInvoiceFileBlob) => {
        if (!filePath) return;
        setViewingFile(true);
        try {
            const response = await fetchBlob(filePath);
            const blobUrl = URL.createObjectURL(response.data);
            const win = window.open(blobUrl, '_blank');
            if (win) {
                setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
            } else {
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = fileName || 'invoice';
                a.click();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
        } catch {
            toast.error(t('supplier_invoices.file_view_error'));
        } finally {
            setViewingFile(false);
        }
    };

    // FX preview — debounced, mirrors day-receipt-form.jsx's per-row pattern
    // but singular since only one invoice is edited at a time here.
    useEffect(() => {
        if (!invoiceDialogOpen) return undefined;
        const amount = parseFloat(invoiceForm.amount);
        const shouldFetch =
            invoiceForm.currency && invoiceForm.currency !== 'SEK' && amount > 0 && !isNaN(amount);

        if (!shouldFetch) {
            setFxPreview(null);
            return undefined;
        }

        setFxPreview({ status: 'loading' });
        let cancelled = false;
        const timer = setTimeout(async () => {
            if (cancelled) return;
            try {
                const res = await getFxPreview({
                    from: invoiceForm.currency,
                    date: invoiceForm.invoice_date,
                    amount,
                });
                if (cancelled) return;
                const { amount_sek, exchange_rate, rate_date } = res.data;
                setFxPreview({ status: 'success', amount_sek, exchange_rate, rate_date });
            } catch {
                if (cancelled) return;
                setFxPreview({ status: 'error' });
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceDialogOpen, invoiceForm.amount, invoiceForm.currency, invoiceForm.invoice_date]);

    // getApiErrorMessage (lib/errors.js) RESOLVES backend "detail" i18n keys
    // itself — a detail starting with "error." is run through i18n.t() at that
    // single choke point, so every backend validation key this form can trigger
    // (payment_status_invalid, vat_amount_invalid, project_not_found, …)
    // reaches the toast already translated. Callers must NOT translate it
    // again; they only supply the fallback message (finding 5.138 — the
    // comment that used to sit here claimed the opposite).
    const handleSaveInvoice = async () => {
        if (!invoiceForm.supplier_id) {
            toast.error(t('supplier_invoices.supplier_required'));
            return;
        }
        // Project is required going forward, EXCEPT editing a legacy invoice
        // that never had one (project_id null on the record being edited) —
        // those must stay editable without being forced to pick a project.
        const isLegacyEdit = !!editingInvoice && !editingInvoice.project_id;
        if (!isLegacyEdit && !invoiceForm.project_id) {
            toast.error(t('supplier_invoices.project_required'));
            return;
        }
        if (!invoiceForm.invoice_date) {
            toast.error(t('supplier_invoices.invoice_date_required'));
            return;
        }
        const amountNum = parseFloat(invoiceForm.amount);
        if (!amountNum || amountNum <= 0 || isNaN(amountNum)) {
            toast.error(t('supplier_invoices.amount_required'));
            return;
        }
        // Moms is OPTIONAL: an untouched field is "not recorded" and goes over
        // as null, never 0. When it IS filled the same rule the backend
        // enforces (0 <= moms <= totalt) is checked here too, so the admin is
        // told before a round trip — same message either way.
        const vatRaw = invoiceForm.vat_amount.trim();
        let vatNum = null;
        if (vatRaw !== '') {
            vatNum = parseFloat(vatRaw);
            if (isNaN(vatNum) || vatNum < 0 || vatNum > amountNum) {
                toast.error(t('error.supplier_invoices.vat_amount_invalid'));
                return;
            }
        }

        // "Paid" with no paid date is not a state this form may produce.
        // The save path deliberately omits payment_status when the status is
        // paid, letting the backend derive it from the DATE — so a blank date
        // asserts nothing and the invoice would silently stay unpaid while
        // this form showed it as paid. Blocking is the honest answer, not
        // filling a date in for the admin: routes/supplier_invoices.py holds
        // the matching rule that a paid date is never fabricated, because a
        // payment date is a financial fact and an empty "Betald den" is more
        // truthful than an invented one.
        //
        // This also catches legacy rows stored paid with no date at all (the
        // register defaulted to paid before Phase 22) — they are made coherent
        // the first time anyone edits them, with the real date the admin knows.
        if (invoiceForm.payment_status === 'paid' && !invoiceForm.paid_date) {
            toast.error(t('supplier_invoices.paid_date_required'));
            return;
        }

        // Activity (Phase 23, PRD 12.1/12.2) — mirrors day-receipt-form.jsx's
        // create/update split (23.R3/R4) exactly: re-validated only on a new
        // invoice, a project change, or an actual activity change, so an
        // unrelated edit (note, amount, file) on a legacy invoice stays
        // permissive even if its activity has since been deleted from the
        // project (Principle 2, "No Data Loss, Ever").
        const isNewInvoice = !editingInvoice;
        const invoiceProjectChanged =
            !editingInvoice || invoiceForm.project_id !== (editingInvoice.project_id || '');
        const invoiceActivityChanged =
            !editingInvoice ||
            String(invoiceForm.activity_id ?? '') !== String(editingInvoice.activity_id ?? '');
        const invoiceActivityMustBeValidated =
            isNewInvoice || invoiceProjectChanged || invoiceActivityChanged;
        if (invoiceActivityMustBeValidated && invoiceForm.project_id) {
            const projectActivities = selectedInvoiceProject?.activities || [];
            if (projectActivities.length > 0 && !invoiceForm.activity_id) {
                toast.error(t('error.activities.required'));
                return;
            }
        }

        setSavingInvoice(true);
        try {
            const basePayload = {
                supplier_id: invoiceForm.supplier_id,
                invoice_number: invoiceForm.invoice_number.trim() || null,
                invoice_date: invoiceForm.invoice_date,
                due_date: invoiceForm.due_date || null,
                payment_reference: invoiceForm.payment_reference.trim() || null,
                amount: amountNum,
                vat_amount: vatNum,
                currency: invoiceForm.currency,
                note: invoiceForm.note.trim() || '',
            };
            // payment_method is deliberately ABSENT from the payload — the
            // control is gone (PRD 11.4) and PUT leaves an omitted optional
            // field untouched, so a value stored before this change survives.
            // Sending null instead would silently delete it.

            // PAID DATE / PAYMENT STATUS — the backend derives "a paid date
            // means paid" itself (PUT with a non-empty paid_date and no
            // explicit payment_status marks the invoice paid). That derivation
            // is NOT re-implemented here; this form only decides when to
            // override it, because an explicit payment_status always wins:
            //
            //   unpaid/scheduled → sent explicitly, with paid_date null. Only an explicit
            //            non-paid status can move an invoice back off paid and clear
            //            its date; the derivation cannot express that.
            //   paid   → the paid DATE is sent and payment_status is OMITTED,
            //            letting the backend derive the status from it. If the
            //            date is somehow blank (a legacy record stored as paid
            //            with none), nothing is asserted at all and the stored
            //            status is left exactly as it is — rather than writing
            //            back a paid invoice with no paid date.
            //
            // POST is different: create has no such derivation, so a new
            // invoice always sends payment_status explicitly (see below).
            if (invoiceForm.payment_status !== 'paid') {
                basePayload.payment_status = invoiceForm.payment_status;
                basePayload.paid_date = null;
            } else {
                basePayload.paid_date = invoiceForm.paid_date || null;
            }
            // The project is the ONLY way to set an invoice's cost center now —
            // the backend no longer accepts cost_code_id at all. Omit the key
            // entirely (not even null) when editing a legacy invoice for which
            // no project was picked, so its own stored cost center is left alone.
            if (invoiceForm.project_id) {
                basePayload.project_id = invoiceForm.project_id;
            }
            // Activity — create always sends it (isNewInvoice above); an
            // edit sends it only when it actually changed, so a routine edit
            // that touches neither project nor activity never re-triggers
            // the server's activity validation (same reasoning as 23.R4).
            if (isNewInvoice) {
                basePayload.activity_id = invoiceForm.activity_id || null;
            } else if (invoiceActivityChanged) {
                basePayload.activity_id = invoiceForm.activity_id || null;
            }

            // Persist the core invoice fields FIRST, without touching the file.
            // This is deliberately ordered before any upload so a validation or
            // FX-conversion failure here never leaves an uploaded file orphaned
            // on disk with nothing in the database referencing it.
            let invoiceId;
            if (editingInvoice) {
                invoiceId = editingInvoice.id;
                await updateSupplierInvoice(invoiceId, {
                    ...basePayload,
                    file_path: invoiceForm.file_path,
                    file_name: invoiceForm.file_name,
                });
            } else {
                // POST has no "a paid date means paid" derivation and defaults
                // payment_status to "paid" server-side, so create must state
                // the status explicitly — otherwise a new invoice entered as
                // unpaid would be stored as paid.
                const createRes = await createSupplierInvoice({
                    ...basePayload,
                    payment_status: invoiceForm.payment_status,
                    // A held document's file is ALREADY stored (Phase 25.4),
                    // so it rides along on create instead of going through the
                    // upload-then-attach step below — there is nothing to
                    // upload. Only ever set by the intake hand-off; a normal
                    // new invoice opens with file_path null.
                    ...(invoiceForm.file_path && !pendingFile
                        ? { file_path: invoiceForm.file_path, file_name: invoiceForm.file_name }
                        : {}),
                });
                invoiceId = createRes.data.id;
                // The invoice now exists, which is the fact the held document
                // was waiting for. Recorded here rather than after the file
                // step, so a failed attachment cannot leave the queue offering
                // a document that HAS already become an invoice.
                await recordIntakeConfirmation(invoiceId);
            }

            // The invoice now safely exists. Attaching a newly-picked file is a
            // separate, narrower step — if it fails, the already-saved invoice
            // is not rolled back; the admin can reopen it and retry the upload.
            if (pendingFile) {
                try {
                    // Files are stored under the INVOICE's own year/month, not the
                    // currently-browsed month, so a backdated upload lands correctly.
                    const invDate = parseISODateLocal(invoiceForm.invoice_date);
                    const uploadRes = await uploadSupplierInvoiceFile(
                        pendingFile,
                        invDate.getFullYear(),
                        invDate.getMonth() + 1
                    );
                    await updateSupplierInvoice(invoiceId, {
                        file_path: uploadRes.data.file_path,
                        file_name: uploadRes.data.file_name,
                    });
                } catch {
                    toast.error(t('supplier_invoices.file_attach_failed_after_save'));
                    setInvoiceDialogOpen(false);
                    setPendingFile(null);
                    fetchAll();
                    return;
                }
            }

            toast.success(
                editingInvoice
                    ? t('supplier_invoices.invoice_updated')
                    : t('supplier_invoices.invoice_created')
            );
            setInvoiceDialogOpen(false);
            setPendingFile(null);
            fetchAll();
        } catch (error) {
            const isFxOutage =
                error?.response?.status === 503 &&
                error?.response?.data?.detail?.error_code === 'error.fx.unavailable';
            if (isFxOutage) {
                toast.error(t('supplier_invoices.fx_failure_short'), {
                    description: t('supplier_invoices.fx_failure_hint'),
                    action: {
                        label: t('supplier_invoices.fx_lookup_link'),
                        onClick: () => window.open(RIKSBANK_URL, '_blank'),
                    },
                });
            } else {
                toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_save')));
            }
        } finally {
            setSavingInvoice(false);
        }
    };

    // ── Mark as paid (row action) ────────────────────────────────────────────
    // Shared by the month list and the Outstanding panel, then refetches
    // (fetchAll reloads both the browsed month and the cross-month Outstanding
    // list together).
    //
    // The paid date defaults to the invoice's DUE DATE, not today: the month's
    // invoices are settled in one bank run and the payment is booked on the
    // förfallodatum. This is the date that eventually reaches Fortnox, so it
    // has to be the real one. Only an invoice with no due date at all falls
    // back to today. payment_status is sent EXPLICITLY here because this
    // button's intent is unambiguous — it is the act of marking paid.
    // The row checkmark moves an invoice one step: unpaid -> scheduled
    // (upplagd för betalning, the bank run) -> paid.
    const handleMarkAsPaid = async (inv) => {
        setMarkingPaidId(inv.id);
        try {
            if (inv.payment_status === 'unpaid') {
                await updateSupplierInvoice(inv.id, { payment_status: 'scheduled' });
                toast.success(t('supplier_invoices.marked_scheduled'));
            } else {
                await updateSupplierInvoice(inv.id, {
                    payment_status: 'paid',
                    paid_date: inv.due_date || toLocalISODate(new Date()),
                });
                toast.success(t('supplier_invoices.marked_paid'));
            }
            fetchAll();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_mark_paid')));
        } finally {
            setMarkingPaidId(null);
        }
    };

    // ── Delete invoice ───────────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteSupplierInvoice(deleteTarget.id);
            toast.success(t('supplier_invoices.invoice_deleted'));
            setDeleteTarget(null);
            fetchAll();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_delete')));
        } finally {
            setDeleting(false);
        }
    };

    // ── Manage suppliers ─────────────────────────────────────────────────────
    const openAddSupplierForm = () => {
        setEditingSupplierId(null);
        setSupplierForm(emptySupplierForm());
    };

    const openEditSupplierForm = (supplier) => {
        setEditingSupplierId(supplier.id);
        setSupplierForm({
            name: supplier.name,
            org_number: supplier.org_number || '',
            default_project_id: supplier.default_project_id || '',
            notes: supplier.notes || '',
        });
    };

    const handleManageSuppliersOpenChange = (open) => {
        setManageSuppliersOpen(open);
        if (!open) {
            setEditingSupplierId(null);
            setSupplierForm(emptySupplierForm());
        }
    };

    const handleSaveSupplier = async () => {
        if (!supplierForm.name.trim()) {
            toast.error(t('supplier_invoices.supplier_name_required'));
            return;
        }
        setSavingSupplier(true);
        try {
            const payload = {
                name: supplierForm.name.trim(),
                org_number: supplierForm.org_number.trim() || null,
                default_project_id: supplierForm.default_project_id || null,
                notes: supplierForm.notes || '',
            };
            if (editingSupplierId) {
                const res = await updateSupplier(editingSupplierId, payload);
                setSuppliers((prev) =>
                    prev.map((s) => (s.id === editingSupplierId ? res.data : s))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
                toast.success(t('supplier_invoices.supplier_updated'));
            } else {
                const res = await createSupplier(payload);
                setSuppliers((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                toast.success(t('supplier_invoices.supplier_created', { name: payload.name }));
            }
            setEditingSupplierId(null);
            setSupplierForm(emptySupplierForm());
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_save_supplier')));
        } finally {
            setSavingSupplier(false);
        }
    };

    const handleToggleSupplierActive = async (supplier) => {
        try {
            const res = await updateSupplier(supplier.id, { is_active: !supplier.is_active });
            setSuppliers((prev) => prev.map((s) => (s.id === supplier.id ? res.data : s)));
            toast.success(
                res.data.is_active
                    ? t('supplier_invoices.supplier_activated', { name: supplier.name })
                    : t('supplier_invoices.supplier_deactivated', { name: supplier.name })
            );
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('supplier_invoices.failed_update_supplier')));
        }
    };

    // The invoice dialog only becomes two-pane when there is actually a
    // document to read alongside the form — a newly-picked file (previewed
    // before it is uploaded) or one already stored on the invoice. With no
    // attachment the dialog stays exactly as it was; no permanent empty half.
    const hasInvoiceDocument = !!pendingFile || !!invoiceForm.file_path;

    // Which file endpoint can serve the document currently on the dialog. Both
    // are module-level references, never inline arrows — SupplierInvoicePreview
    // takes this as an effect dependency.
    const intakeFileFetcher = intake ? getInboundDocumentFileBlob : getSupplierInvoiceFileBlob;

    // A quiet per-field marker saying "this value was read off the attached
    // document — worth a glance" (PRD 13.3). Not a banner: a partial fill is
    // the normal case and a banner announcing it every time would be noise.
    //
    // It is DERIVED, never bookkept: the marker shows while the live field
    // still equals what the document said, so the moment the user edits that
    // field the two differ and the marker disappears on its own. No onChange
    // anywhere needs to know this feature exists.
    const fromDocumentMarker = (extracted, form, key) => {
        if (!extracted || extracted[key] === undefined || extracted[key] === null) return null;
        if (String(form[key] ?? '') !== String(extracted[key])) return null;
        return (
            <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                {t('supplier_invoices.from_document')}
            </span>
        );
    };

    // Kostnadsställe is DERIVED and read-only: it comes from the picked
    // project, or — for a legacy invoice that never had one — from the value
    // already stored on the invoice. It is never independently settable.
    //
    // Fortnox reads the cost CODE, never the project's own code and never the
    // cost_code_id (a raw UUID) — so this field shows cost_code_code only,
    // and is empty (placeholder shown) when no code is available at all. The
    // cost center's NAME stays where it already was: the hint text below.
    const costCenterDisplay = (invoiceForm.project_id && selectedInvoiceProject?.cost_code_code) ? selectedInvoiceProject.cost_code_code : (invoiceForm.cost_code_code || '');

    if (loading && invoices.length === 0 && suppliers.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="supplier-invoices-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {t('supplier_invoices.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('supplier_invoices.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        className="uppercase tracking-wider"
                        onClick={() => setManageSuppliersOpen(true)}
                        data-testid="manage-suppliers-btn"
                    >
                        <Users className="w-4 h-4 mr-2" />
                        {t('supplier_invoices.manage_suppliers')}
                    </Button>
                    <Button
                        className="uppercase tracking-wider"
                        onClick={openCreateInvoiceDialog}
                        data-testid="add-supplier-invoice-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('supplier_invoices.add_invoice')}
                    </Button>
                </div>
            </div>

            {/* Month navigation — the shared navigator (Task 22.6). The month
                was previously squeezed into the left half of this bar with the
                totals occupying the right; the totals are cards now (22.7) and
                the bar carries nothing but the navigation. */}
            <MonthNavigator
                year={year}
                month={month}
                onPrev={() => navigateMonth('prev')}
                onNext={() => navigateMonth('next')}
            />

            {/* Summary cards for the BROWSED MONTH (Task 22.7). Every figure
                here is scoped to the month named in the navigator directly
                above. The cross-month picture is the Outstanding panel below,
                which labels itself as such. */}
            <SummaryCardRow data-testid="supplier-invoice-summary">
                <SummaryCard
                    icon={FileText}
                    label={t('supplier_invoices.card_invoice_count')}
                    value={invoices.length}
                    valueTestId="invoice-count"
                />
                <SummaryCard
                    icon={Clock}
                    label={t('supplier_invoices.unpaid_total')}
                    value={formatSEK(unpaidTotal)}
                    variant={unpaidTotal > 0 ? 'warning' : 'default'}
                    valueTestId="unpaid-total"
                />
                <SummaryCard
                    icon={AlertTriangle}
                    label={t('supplier_invoices.card_overdue_count')}
                    value={monthOverdueCount}
                    variant={monthOverdueCount > 0 ? 'danger' : 'default'}
                    valueTestId="month-overdue-count"
                />
                <SummaryCard
                    icon={DollarSign}
                    label={t('supplier_invoices.grand_total')}
                    value={formatSEK(grandTotal)}
                    variant="money"
                    valueTestId="grand-total"
                />
            </SummaryCardRow>

            {/* Outstanding — cross-month unpaid invoices. An unpaid March bill
                must not become invisible in September; this panel is the fix. */}
            {outstanding.length > 0 && (
                <Card className="border border-amber-300 bg-amber-50/60 shadow-none" data-testid="outstanding-panel">
                    <button
                        type="button"
                        onClick={() => setOutstandingOpen((prev) => !prev)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                        aria-expanded={outstandingOpen}
                        data-testid="outstanding-panel-toggle"
                    >
                        <div className="flex items-center gap-2 flex-wrap">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="font-heading text-sm font-semibold uppercase tracking-wider text-amber-900">
                                {t('supplier_invoices.outstanding_header', {
                                    count: outstanding.length,
                                    amount: formatSEK(outstandingTotal),
                                })}
                            </span>
                            {outstandingOverdueCount > 0 && (
                                // Finding 5.171: a Badge renders a <div>, which is not
                                // valid phrasing content inside this <button> — the
                                // browser reparents it, leaving the button's
                                // accessible name and hit area unreliable. A <span>
                                // styled with the same destructive Badge classes
                                // keeps the look without breaking the button.
                                <span className={badgeVariants({ variant: 'destructive' })}>
                                    {t('supplier_invoices.overdue_count', { count: outstandingOverdueCount })}
                                </span>
                            )}
                        </div>
                        <ChevronDown
                            className={`w-4 h-4 text-amber-700 shrink-0 transition-transform ${outstandingOpen ? '' : '-rotate-90'}`}
                        />
                    </button>
                    {outstandingOpen && (
                        <CardContent className="p-0 border-t border-amber-200">
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="data-table min-w-[800px]">
                                    <thead>
                                        <tr>
                                            <th>{t('supplier_invoices.col_supplier')}</th>
                                            <th>{t('supplier_invoices.col_project')}</th>
                                            <th>{t('supplier_invoices.col_ocr')}</th>
                                            <th>{t('supplier_invoices.col_due')}</th>
                                            <th>{t('common.amount')}</th>
                                            <th className="text-right">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {outstanding.map((inv) => {
                                            const overdue = isOverdueInvoice(inv, todayISO);
                                            return (
                                                <tr key={inv.id}>
                                                    <td className="font-medium">
                                                        {inv.supplier_name}
                                                        {inv.invoice_number && (
                                                            <div className="text-xs text-muted-foreground font-mono font-normal">
                                                                {t('supplier_invoices.invoice_number_short', { number: inv.invoice_number })}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-sm text-muted-foreground">{inv.project_name || '—'}</td>
                                                    <td className="font-mono text-sm">{inv.payment_reference || '—'}</td>
                                                    <td
                                                        className={`font-mono text-sm whitespace-nowrap ${
                                                            overdue ? 'text-destructive font-semibold' : ''
                                                        }`}
                                                    >
                                                        {inv.due_date ? formatDate(inv.due_date) : '—'}
                                                        {overdue && (
                                                            <div className="text-[10px] uppercase tracking-wider">
                                                                {t('supplier_invoices.overdue')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="font-mono">
                                                        {formatNumber(inv.amount, 2)} {inv.currency}
                                                        {inv.currency !== 'SEK' && (
                                                            <div className="text-xs text-muted-foreground font-normal">
                                                                ≈ {formatSEK(inv.amount_sek)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-right whitespace-nowrap">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleMarkAsPaid(inv)}
                                                            disabled={markingPaidId === inv.id}
                                                            aria-label={t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                            title={t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                            data-testid={`mark-paid-outstanding-${inv.id}`}
                                                        >
                                                            {markingPaidId === inv.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Check className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditInvoiceDialog(inv)}
                                                            aria-label={t('common.edit')}
                                                            data-testid={`edit-outstanding-${inv.id}`}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden p-3 space-y-3">
                                {outstanding.map((inv) => {
                                    const overdue = isOverdueInvoice(inv, todayISO);
                                    return (
                                        <div key={inv.id} className="border border-amber-200 bg-background p-4 space-y-2">
                                            <div className="flex justify-between items-start gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-medium truncate">{inv.supplier_name}</p>
                                                    {inv.project_name && (
                                                        <p className="text-xs text-muted-foreground truncate">{inv.project_name}</p>
                                                    )}
                                                    <p
                                                        className={`text-xs font-mono ${
                                                            overdue ? 'text-destructive font-semibold' : 'text-muted-foreground'
                                                        }`}
                                                    >
                                                        {inv.due_date ? formatDate(inv.due_date) : '—'}
                                                        {overdue ? ` · ${t('supplier_invoices.overdue')}` : ''}
                                                    </p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="font-mono font-semibold">
                                                        {formatNumber(inv.amount, 2)} {inv.currency}
                                                    </p>
                                                    {inv.currency !== 'SEK' && (
                                                        <p className="text-xs text-muted-foreground">
                                                            ≈ {formatSEK(inv.amount_sek)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-end gap-1 pt-2 border-t border-amber-100">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-h-[44px]"
                                                    onClick={() => handleMarkAsPaid(inv)}
                                                    disabled={markingPaidId === inv.id}
                                                >
                                                    {markingPaidId === inv.id ? (
                                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                    ) : (
                                                        <Check className="w-4 h-4 mr-1" />
                                                    )}
                                                    {t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-h-[44px] min-w-[44px] p-0"
                                                    onClick={() => openEditInvoiceDialog(inv)}
                                                    aria-label={t('common.edit')}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Status filter — client-side over the already-loaded month.
                Finding 5.170: the Label is tied to the SelectTrigger by id so
                clicking it focuses the control and screen readers announce it. */}
            <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Label htmlFor="si-status-filter" className="label-uppercase text-xs text-muted-foreground">
                        {t('supplier_invoices.filter_label')}
                    </Label>
                    <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                        <SelectTrigger id="si-status-filter" className="w-40" data-testid="supplier-invoice-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('supplier_invoices.filter_all')}</SelectItem>
                            <SelectItem value="unpaid">{t('supplier_invoices.filter_unpaid')}</SelectItem>
                            <SelectItem value="scheduled">{t('supplier_invoices.filter_scheduled')}</SelectItem>
                            <SelectItem value="paid">{t('supplier_invoices.filter_paid')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
                </div>
            ) : filteredInvoices.length === 0 ? (
                <Card className="border border-border shadow-none">
                    <CardContent className="p-8 text-center text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p className="mb-4">
                            {invoices.length === 0
                                ? t('supplier_invoices.no_invoices')
                                : t('supplier_invoices.no_invoices_filtered')}
                        </p>
                        {invoices.length === 0 ? (
                            <Button
                                className="uppercase tracking-wider"
                                onClick={openCreateInvoiceDialog}
                                data-testid="add-supplier-invoice-empty-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('supplier_invoices.add_invoice')}
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                className="uppercase tracking-wider"
                                onClick={() => {
                                    handleStatusFilterChange('all');
                                }}
                                data-testid="clear-status-filter-btn"
                            >
                                {t('supplier_invoices.clear_filter')}
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {groups.map((group) => (
                        <Card key={group.key} className="border border-border shadow-none">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                                <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
                                    {group.name}
                                </h3>
                                <span className="font-mono font-bold">{formatSEK(group.subtotal)}</span>
                            </div>
                            <CardContent className="p-0">
                                {/* Desktop table */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="data-table min-w-[1200px]">
                                        <thead>
                                            <tr>
                                                <th>{t('supplier_invoices.col_doc_type')}</th>
                                                <th>{t('common.date')}</th>
                                                <th>{t('supplier_invoices.col_supplier')}</th>
                                                <th>{t('supplier_invoices.col_project')}</th>
                                                <th>{t('supplier_invoices.col_ocr')}</th>
                                                <th>{t('common.amount')}</th>
                                                <th>{t('supplier_invoices.col_due')}</th>
                                                <th>{t('supplier_invoices.col_status')}</th>
                                                <th className="text-center">{t('supplier_invoices.col_note')}</th>
                                                <th className="text-center">{t('supplier_invoices.col_file')}</th>
                                                <th className="text-right">{t('common.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.items.map((inv) => {
                                                const notPaid = (inv.payment_status || 'paid') !== 'paid';
                                                const overdue = isOverdueInvoice(inv, todayISO);
                                                const isReceipt = (inv.doc_type || 'invoice') === 'receipt';
                                                return (
                                                <tr key={inv.id}>
                                                    <td>
                                                        <Badge variant="outline" data-testid={`doc-type-badge-${inv.id}`}>
                                                            {isReceipt
                                                                ? t('supplier_invoices.doc_type_receipt')
                                                                : t('supplier_invoices.doc_type_invoice')}
                                                        </Badge>
                                                    </td>
                                                    <td className="font-mono text-sm whitespace-nowrap">
                                                        {formatDate(inv.invoice_date)}
                                                    </td>
                                                    <td className="font-medium">
                                                        {inv.supplier_name}
                                                        {inv.invoice_number && (
                                                            <div className="text-xs text-muted-foreground font-mono font-normal">
                                                                {t('supplier_invoices.invoice_number_short', { number: inv.invoice_number })}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-sm text-muted-foreground">{inv.project_name || '—'}</td>
                                                    <td className="font-mono text-sm">{inv.payment_reference || '—'}</td>
                                                    <td className="font-mono">
                                                        {formatNumber(inv.amount, 2)} {inv.currency}
                                                        {inv.currency !== 'SEK' && (
                                                            <div className="text-xs text-muted-foreground font-normal">
                                                                ≈ {formatSEK(inv.amount_sek)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`font-mono text-sm whitespace-nowrap ${
                                                            overdue ? 'text-destructive font-semibold' : ''
                                                        }`}
                                                    >
                                                        {inv.due_date ? formatDate(inv.due_date) : '—'}
                                                        {overdue && (
                                                            <div className="text-[10px] uppercase tracking-wider">
                                                                {t('supplier_invoices.overdue')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <PaymentStatusBadge inv={inv} overdue={overdue} t={t} />
                                                    </td>
                                                    <td className="text-center">
                                                        {inv.note ? (
                                                            <StickyNote
                                                                className="w-4 h-4 mx-auto text-muted-foreground"
                                                                aria-label={t('supplier_invoices.has_note')}
                                                            >
                                                                <title>{inv.note}</title>
                                                            </StickyNote>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {inv.file_path ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleViewFile(inv.file_path, inv.file_name)}
                                                                disabled={viewingFile}
                                                                aria-label={t('supplier_invoices.view_file')}
                                                            >
                                                                <Paperclip className="w-4 h-4" />
                                                            </Button>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                    <td className="text-right whitespace-nowrap">
                                                        {notPaid && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleMarkAsPaid(inv)}
                                                                disabled={markingPaidId === inv.id}
                                                                aria-label={t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                                title={t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                                data-testid={`mark-paid-supplier-invoice-${inv.id}`}
                                                            >
                                                                {markingPaidId === inv.id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Check className="w-4 h-4" />
                                                                )}
                                                            </Button>
                                                        )}
                                                        {/* A legacy supplier receipt (KD-093) has no edit
                                                            dialog any more — view file and delete only. */}
                                                        {!isReceipt && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => openEditInvoiceDialog(inv)}
                                                                aria-label={t('common.edit')}
                                                                data-testid={`edit-supplier-invoice-${inv.id}`}
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setDeleteTarget(inv)}
                                                            className="text-destructive hover:text-destructive"
                                                            aria-label={t('common.delete')}
                                                            data-testid={`delete-supplier-invoice-${inv.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile cards */}
                                <div className="md:hidden p-3 space-y-3">
                                    {group.items.map((inv) => {
                                        const notPaid = (inv.payment_status || 'paid') !== 'paid';
                                        const overdue = isOverdueInvoice(inv, todayISO);
                                        const isReceipt = (inv.doc_type || 'invoice') === 'receipt';
                                        return (
                                        <div key={inv.id} className="border border-border p-4 space-y-2">
                                            <div className="flex justify-between items-start gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" data-testid={`doc-type-badge-mobile-${inv.id}`}>
                                                            {isReceipt
                                                                ? t('supplier_invoices.doc_type_receipt')
                                                                : t('supplier_invoices.doc_type_invoice')}
                                                        </Badge>
                                                        <p className="font-medium truncate">{inv.supplier_name}</p>
                                                    </div>
                                                    {inv.project_name && (
                                                        <p className="text-xs text-muted-foreground truncate">{inv.project_name}</p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground font-mono">
                                                        {formatDate(inv.invoice_date)}
                                                        {inv.invoice_number ? ` · ${inv.invoice_number}` : ''}
                                                        {inv.payment_reference ? ` · OCR ${inv.payment_reference}` : ''}
                                                    </p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="font-mono font-semibold">
                                                        {formatNumber(inv.amount, 2)} {inv.currency}
                                                    </p>
                                                    {inv.currency !== 'SEK' && (
                                                        <p className="text-xs text-muted-foreground">
                                                            ≈ {formatSEK(inv.amount_sek)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <PaymentStatusBadge inv={inv} overdue={overdue} t={t} />
                                                {inv.due_date && (
                                                    <span
                                                        className={`text-xs font-mono ${
                                                            overdue ? 'text-destructive font-semibold' : 'text-muted-foreground'
                                                        }`}
                                                    >
                                                        {t('supplier_invoices.due_short', { date: formatDate(inv.due_date) })}
                                                    </span>
                                                )}
                                            </div>
                                            {inv.note && (
                                                <p className="text-xs text-muted-foreground italic">{inv.note}</p>
                                            )}
                                            <div className="flex items-center justify-between pt-2 border-t border-border">
                                                <div>
                                                    {inv.file_path && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="min-h-[44px]"
                                                            onClick={() => handleViewFile(inv.file_path, inv.file_name)}
                                                            disabled={viewingFile}
                                                        >
                                                            <Paperclip className="w-4 h-4 mr-1" />
                                                            {t('supplier_invoices.view_file')}
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {notPaid && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="min-h-[44px] min-w-[44px] p-0"
                                                            onClick={() => handleMarkAsPaid(inv)}
                                                            disabled={markingPaidId === inv.id}
                                                            aria-label={t(inv.payment_status === 'unpaid' ? 'supplier_invoices.mark_as_scheduled' : 'supplier_invoices.mark_as_paid')}
                                                        >
                                                            {markingPaidId === inv.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Check className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                    )}
                                                    {!isReceipt && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="min-h-[44px] min-w-[44px] p-0"
                                                            onClick={() => openEditInvoiceDialog(inv)}
                                                            aria-label={t('common.edit')}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="min-h-[44px] min-w-[44px] p-0 text-destructive hover:text-destructive"
                                                        onClick={() => setDeleteTarget(inv)}
                                                        aria-label={t('common.delete')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Add/Edit Invoice Dialog */}
            <Dialog
                open={invoiceDialogOpen}
                onOpenChange={(open) => {
                    setInvoiceDialogOpen(open);
                    // Abandoning the dialog abandons the hand-off with it —
                    // the held document stays pending and is offered again.
                    // Only reached on a user-driven close (Esc / overlay / X);
                    // a programmatic close after a successful save does not
                    // fire this, so the confirmation is never cancelled here.
                    if (!open) setIntake(null);
                }}
            >
                {/* Widens to two panes only when there is a document to read
                    alongside the form; otherwise it is the same dialog as before. */}
                <DialogContent className={hasInvoiceDocument ? 'max-w-5xl' : 'max-w-lg'}>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingInvoice
                                ? t('supplier_invoices.edit_invoice')
                                : t('supplier_invoices.new_invoice')}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Below lg this is ONE scroll container holding the form
                        and (when opened) the stacked document beneath it. From
                        lg up it becomes two columns that scroll independently. */}
                    <div
                        className={cn(
                            'py-2 max-h-[65vh] overflow-y-auto',
                            hasInvoiceDocument &&
                                'lg:max-h-none lg:overflow-visible lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start'
                        )}
                    >
                        {/* ── Form column ──────────────────────────────────────
                            Two columns from sm up; only genuinely long fields
                            (note, file row, the FX line) span the full width. */}
                        <div
                            className={cn(
                                'grid grid-cols-1 sm:grid-cols-2 gap-4',
                                hasInvoiceDocument && 'lg:max-h-[65vh] lg:overflow-y-auto lg:pr-1'
                            )}
                        >
                            {/* Supplier combobox */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-supplier" className="label-uppercase">
                                    {t('supplier_invoices.supplier_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'supplier_id')}
                                </Label>
                                <Popover open={supplierPickerOpen} onOpenChange={setSupplierPickerOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="si-supplier"
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={supplierPickerOpen}
                                            className="w-full justify-between font-normal"
                                            data-testid="supplier-invoice-supplier-trigger"
                                        >
                                            <span className="truncate">
                                                {invoiceForm.supplier_name || t('supplier_invoices.supplier_placeholder')}
                                            </span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popper-anchor-width] p-0" align="start">
                                        <Command>
                                            <CommandInput
                                                placeholder={t('supplier_invoices.supplier_search_placeholder')}
                                                value={supplierQuery}
                                                onValueChange={setSupplierQuery}
                                            />
                                            <CommandList>
                                                <CommandEmpty>
                                                    <span className="text-muted-foreground text-sm px-2">
                                                        {t('supplier_invoices.no_suppliers_found')}
                                                    </span>
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {activeSuppliers.map((s) => (
                                                        <CommandItem
                                                            key={s.id}
                                                            value={s.name}
                                                            onSelect={() => handleSelectSupplier(s)}
                                                            data-testid={`supplier-option-${s.id}`}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    'mr-2 h-4 w-4',
                                                                    invoiceForm.supplier_id === s.id
                                                                        ? 'opacity-100'
                                                                        : 'opacity-0'
                                                                )}
                                                            />
                                                            <span className="truncate">{s.name}</span>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                                <div className="border-t border-border mt-1 pt-1">
                                                    <button
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2 disabled:opacity-50 text-primary"
                                                        onClick={openQuickSupplier}
                                                        data-testid="supplier-quick-create-btn"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                        {supplierQuery.trim()
                                                            ? t('supplier_invoices.add_new_supplier', { name: supplierQuery.trim() })
                                                            : t('supplier_invoices.add_new_supplier_generic')}
                                                    </button>
                                                </div>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Invoice number */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-invoice-number" className="label-uppercase">
                                    {t('supplier_invoices.invoice_number_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'invoice_number')}
                                </Label>
                                <Input
                                    id="si-invoice-number"
                                    value={invoiceForm.invoice_number}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, invoice_number: e.target.value }))
                                    }
                                    placeholder={t('supplier_invoices.invoice_number_placeholder')}
                                />
                            </div>

                            {/* Invoice date */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-invoice-date" className="label-uppercase">
                                    {t('supplier_invoices.invoice_date_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'invoice_date')}
                                </Label>
                                <Input
                                    id="si-invoice-date"
                                    type="date"
                                    value={invoiceForm.invoice_date}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, invoice_date: e.target.value }))
                                    }
                                />
                            </div>

                            {/* Due date (förfallodatum) — the day the monthly
                                bank run books this payment. */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-due-date" className="label-uppercase">
                                    {t('supplier_invoices.due_date_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'due_date')}
                                </Label>
                                <Input
                                    id="si-due-date"
                                    type="date"
                                    value={invoiceForm.due_date}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, due_date: e.target.value }))
                                    }
                                />
                            </div>

                            {/* Project — the sole way to set an invoice's cost center */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-project" className="label-uppercase">
                                    {t('supplier_invoices.project_label')}
                                </Label>
                                <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="si-project"
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={projectPickerOpen}
                                            className="w-full justify-between font-normal"
                                            data-testid="supplier-invoice-project-trigger"
                                        >
                                            <span className="truncate">
                                                {invoiceForm.project_name || t('supplier_invoices.project_placeholder')}
                                            </span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popper-anchor-width] p-0" align="start">
                                        <Command>
                                            <CommandInput
                                                placeholder={t('supplier_invoices.project_search_placeholder')}
                                                value={projectQuery}
                                                onValueChange={setProjectQuery}
                                            />
                                            <CommandList>
                                                <CommandEmpty>
                                                    <span className="text-muted-foreground text-sm">
                                                        {t('supplier_invoices.no_projects_found')}
                                                    </span>
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {selectableProjects.map((p) => {
                                                        const blocked = !p.cost_code_id;
                                                        return (
                                                            <CommandItem
                                                                key={p.id}
                                                                value={p.name}
                                                                disabled={blocked}
                                                                onSelect={() => {
                                                                    if (!blocked) handleSelectProject(p);
                                                                }}
                                                                data-testid={`project-option-${p.id}`}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        'mr-2 h-4 w-4',
                                                                        invoiceForm.project_id === p.id
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0'
                                                                    )}
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="truncate">{p.name}</div>
                                                                    {blocked && (
                                                                        <div className="text-[11px] text-amber-600">
                                                                            {t('supplier_invoices.project_needs_cost_center')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {!p.active && (
                                                                    <span className="text-[10px] text-muted-foreground uppercase ml-1 shrink-0">
                                                                        {t('common.inactive')}
                                                                    </span>
                                                                )}
                                                            </CommandItem>
                                                        );
                                                    })}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Kostnadsställe — a real field beside Projekt, but
                                READ-ONLY and derived: the project is the single
                                source of truth for it (a legacy invoice with no
                                project shows its own stored value instead). */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-cost-center" className="label-uppercase">
                                    {t('supplier_invoices.cost_center_label')}
                                </Label>
                                <Input
                                    id="si-cost-center"
                                    value={costCenterDisplay}
                                    readOnly
                                    aria-readonly="true"
                                    placeholder={t('supplier_invoices.no_cost_center')}
                                    className="bg-muted text-muted-foreground cursor-default"
                                    data-testid="supplier-invoice-cost-center"
                                />
                                <p className="text-xs text-muted-foreground" data-testid="derived-cost-center">
                                    {invoiceForm.project_id
                                        ? invoiceForm.cost_code_name || t('supplier_invoices.cost_center_from_project_hint')
                                        : invoiceForm.cost_code_id
                                            ? invoiceForm.cost_code_name || t('supplier_invoices.cost_center_legacy_hint')
                                            : t('supplier_invoices.cost_center_pick_project_hint')}
                                </p>
                            </div>

                            {/* Activity — shown ONLY when the picked project defines
                                any (Phase 23, PRD 12.1/12.2); matches
                                day-receipt-form.jsx's interaction exactly. */}
                            {invoiceForm.project_id && (() => {
                                const projectActivities = selectedInvoiceProject?.activities || [];
                                if (projectActivities.length === 0) return null;
                                return (
                                    <div className="space-y-1.5 min-w-0">
                                        <Label htmlFor="si-activity" className="label-uppercase">
                                            {t('timesheet.bulk_dialog.activity_label')}{' '}
                                            <span className="text-destructive">*</span>
                                        </Label>
                                        <Select
                                            value={invoiceForm.activity_id || ''}
                                            onValueChange={(actId) => {
                                                const act = projectActivities.find((a) => a.id === actId);
                                                setInvoiceForm((prev) => ({
                                                    ...prev,
                                                    activity_id: actId,
                                                    activity_name: act ? act.name : '',
                                                }));
                                            }}
                                        >
                                            <SelectTrigger
                                                id="si-activity"
                                                aria-required="true"
                                                data-testid="supplier-invoice-activity-select"
                                            >
                                                <SelectValue placeholder={t('timesheet.bulk_dialog.activity_placeholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {projectActivities.map((act) => (
                                                    <SelectItem key={act.id} value={act.id}>
                                                        {act.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                );
                            })()}

                            {/* Totalt — the gross total INCLUSIVE of VAT, which
                                is what Fortnox calls Totalt. Same field as
                                before; the label is what changed. */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-total" className="label-uppercase">
                                    {t('supplier_invoices.amount_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'amount')}
                                </Label>
                                <Input
                                    id="si-total"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={invoiceForm.amount}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, amount: e.target.value }))
                                    }
                                    placeholder={t('supplier_invoices.amount_placeholder')}
                                />
                            </div>

                            {/* Currency */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-currency" className="label-uppercase">
                                    {t('supplier_invoices.currency_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'currency')}
                                </Label>
                                <Select
                                    value={invoiceForm.currency}
                                    onValueChange={(v) => setInvoiceForm((prev) => ({ ...prev, currency: v }))}
                                >
                                    <SelectTrigger id="si-currency">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {c}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Moms — sits directly below Totalt because it is a
                                component OF it, not an addition to it. Optional:
                                left empty it is sent as null, never 0. */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-vat" className="label-uppercase">
                                    {t('supplier_invoices.vat_amount_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'vat_amount')}
                                </Label>
                                <Input
                                    id="si-vat"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={invoiceForm.vat_amount}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, vat_amount: e.target.value }))
                                    }
                                    placeholder={t('supplier_invoices.vat_amount_placeholder')}
                                    data-testid="supplier-invoice-vat-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    {t('supplier_invoices.vat_amount_hint')}
                                </p>
                            </div>

                            {/* Betalstatus — first-class: an invoice sits unpaid
                                in the register until the monthly bank run. */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-payment-status" className="label-uppercase">
                                    {t('supplier_invoices.payment_status_label')}
                                </Label>
                                <Select value={invoiceForm.payment_status} onValueChange={handlePaymentStatusChange}>
                                    <SelectTrigger id="si-payment-status" data-testid="supplier-invoice-payment-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unpaid">{t('supplier_invoices.status_unpaid')}</SelectItem>
                                        <SelectItem value="scheduled">{t('supplier_invoices.status_scheduled')}</SelectItem>
                                        <SelectItem value="paid">{t('supplier_invoices.status_paid')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* FX preview (foreign currency only) */}
                            {fxPreview && (
                                <div className="sm:col-span-2">
                                    {fxPreview.status === 'loading' ? (
                                        <p className="text-xs text-muted-foreground">
                                            {t('supplier_invoices.fx_loading')}
                                        </p>
                                    ) : fxPreview.status === 'success' ? (
                                        <p className="text-xs text-muted-foreground">
                                            {t('supplier_invoices.fx_preview', {
                                                amount: formatSEK(fxPreview.amount_sek),
                                                rate: fxPreview.exchange_rate,
                                                rateDate: fxPreview.rate_date,
                                            })}
                                        </p>
                                    ) : (
                                        <div>
                                            <p className="text-xs text-amber-600">
                                                {t('supplier_invoices.fx_failure_short')}{' '}
                                                <a
                                                    href={RIKSBANK_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline"
                                                >
                                                    {t('supplier_invoices.fx_lookup_link')}
                                                </a>
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('supplier_invoices.fx_failure_hint')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Betald den — only once the invoice is actually
                                paid; hidden while it is still unpaid. */}
                            {invoiceForm.payment_status === 'paid' && (
                                <div className="space-y-1.5 min-w-0">
                                    <Label htmlFor="si-paid-date" className="label-uppercase">
                                        {t('supplier_invoices.paid_on_label')}
                                    </Label>
                                    <Input
                                        id="si-paid-date"
                                        type="date"
                                        value={invoiceForm.paid_date}
                                        onChange={(e) =>
                                            setInvoiceForm((prev) => ({ ...prev, paid_date: e.target.value }))
                                        }
                                        data-testid="supplier-invoice-paid-date"
                                    />
                                </div>
                            )}

                            {/* Payment reference (OCR) */}
                            <div className="space-y-1.5 min-w-0">
                                <Label htmlFor="si-payment-reference" className="label-uppercase">
                                    {t('supplier_invoices.payment_reference_label')}
                                    {fromDocumentMarker(extractedInvoice, invoiceForm, 'payment_reference')}
                                </Label>
                                <Input
                                    id="si-payment-reference"
                                    value={invoiceForm.payment_reference}
                                    onChange={(e) =>
                                        setInvoiceForm((prev) => ({ ...prev, payment_reference: e.target.value }))
                                    }
                                    placeholder={t('supplier_invoices.payment_reference_placeholder')}
                                    maxLength={100}
                                />
                            </div>

                            {/* Note — genuinely long, spans the row */}
                            <div className="space-y-1.5 sm:col-span-2 min-w-0">
                                <Label htmlFor="si-note" className="label-uppercase">
                                    {t('supplier_invoices.note_label')}
                                </Label>
                                <Textarea
                                    id="si-note"
                                    value={invoiceForm.note}
                                    onChange={(e) => setInvoiceForm((prev) => ({ ...prev, note: e.target.value }))}
                                    placeholder={t('supplier_invoices.note_placeholder')}
                                    rows={2}
                                />
                            </div>

                            {/* File row — spans the row */}
                            <div className="space-y-2 sm:col-span-2 min-w-0">
                                {/* A section heading, not a control label: the real
                                    <Label htmlFor="si-file"> sits directly above the input
                                    below. Two labels bound to one control makes a screen
                                    reader announce it twice and is ambiguous about which
                                    one names it. */}
                                <p className="label-uppercase block">
                                    {t('supplier_invoices.file_label')}
                                </p>
                                {invoiceForm.file_path && !pendingFile && (
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-muted-foreground truncate max-w-[220px]">
                                            {invoiceForm.file_name}
                                        </span>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleViewFile(invoiceForm.file_path, invoiceForm.file_name)}
                                            disabled={viewingFile}
                                        >
                                            {viewingFile ? (
                                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            ) : (
                                                <ExternalLink className="w-4 h-4 mr-1" />
                                            )}
                                            {t('common.view')}
                                        </Button>
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="si-file" className="text-xs text-muted-foreground mb-1 block">
                                        {invoiceForm.file_path
                                            ? t('supplier_invoices.replace_file')
                                            : t('supplier_invoices.upload_file')}
                                    </Label>
                                    <input
                                        id="si-file"
                                        type="file"
                                        accept={FILE_ACCEPT}
                                        onChange={handleFileChange}
                                        className="w-full text-sm file:mr-4 file:py-1.5 file:px-3 file:border file:border-border file:text-xs file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80"
                                        disabled={savingInvoice}
                                    />
                                    {pendingFile && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <p className="text-xs text-muted-foreground">
                                                {t('supplier_invoices.will_upload')}:{' '}
                                                <span className="font-medium">{pendingFile.name}</span>
                                            </p>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setPendingFile(null)}
                                                className="min-h-[44px] px-2 text-xs text-destructive hover:text-destructive"
                                            >
                                                {t('common.cancel')}
                                            </Button>
                                        </div>
                                    )}
                                    {/* Purely informational, and never a
                                        blocker: the form stays fully usable
                                        while the document is read, and if the
                                        read finds nothing this line simply
                                        goes away without saying so. */}
                                    {extractingInvoice && (
                                        <p className="text-xs text-muted-foreground mt-1" aria-live="polite">
                                            {t('supplier_invoices.reading_document')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Document column ──────────────────────────────────
                            Only rendered when there is a document, so the dialog
                            is unchanged without one. Unmounting it on dialog
                            close is what revokes its object URL. */}
                        {hasInvoiceDocument && (
                            <div className="min-w-0 mt-4 lg:mt-0 flex flex-col">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="lg:hidden mb-2 uppercase tracking-wider"
                                    onClick={() => setPreviewOpenMobile((prev) => !prev)}
                                    aria-expanded={previewOpenMobile}
                                    aria-controls="si-document-preview"
                                    data-testid="toggle-invoice-preview"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    {previewOpenMobile
                                        ? t('supplier_invoices.preview_hide')
                                        : t('supplier_invoices.preview_show')}
                                </Button>
                                <SupplierInvoicePreview
                                    id="si-document-preview"
                                    file={pendingFile}
                                    filePath={pendingFile ? null : invoiceForm.file_path}
                                    fileName={pendingFile ? null : invoiceForm.file_name}
                                    fetchBlob={intakeFileFetcher}
                                    onOpenExternal={
                                        !pendingFile && invoiceForm.file_path
                                            ? () => handleViewFile(invoiceForm.file_path, invoiceForm.file_name, intakeFileFetcher)
                                            : null
                                    }
                                    className={cn(
                                        'h-[50vh] lg:h-[65vh]',
                                        previewOpenMobile ? 'flex' : 'hidden lg:flex'
                                    )}
                                />
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setInvoiceDialogOpen(false);
                                setIntake(null);
                            }}
                            disabled={savingInvoice}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleSaveInvoice}
                            disabled={savingInvoice}
                            data-testid="save-supplier-invoice-btn"
                        >
                            {savingInvoice ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation — wording adapts to the document's own
                doc_type (UI honesty: an invoice and a receipt are different
                documents, so "delete the invoice" is simply false for a
                receipt). */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {(deleteTarget?.doc_type || 'invoice') === 'receipt'
                                ? t('supplier_invoices.delete_title_receipt')
                                : t('supplier_invoices.delete_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {(deleteTarget?.doc_type || 'invoice') === 'receipt'
                                ? t('supplier_invoices.delete_description_receipt', {
                                      payee: deleteTarget?.supplier_name,
                                      date: deleteTarget ? formatDate(deleteTarget.invoice_date) : '',
                                  })
                                : t('supplier_invoices.delete_description', {
                                      supplier: deleteTarget?.supplier_name,
                                      date: deleteTarget ? formatDate(deleteTarget.invoice_date) : '',
                                  })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteConfirm();
                            }}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-supplier-invoice-btn"
                        >
                            {deleting ? t('common.deleting') : t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Quick-create supplier, opened from the invoice's supplier picker. */}
            <Dialog open={!!quickSupplier} onOpenChange={(open) => !open && setQuickSupplier(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{t('supplier_invoices.add_supplier')}</DialogTitle>
                        <DialogDescription className="sr-only">{t('supplier_invoices.add_supplier')}</DialogDescription>
                    </DialogHeader>
                    <form
                        className="space-y-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleCreateSupplierInline();
                        }}
                    >
                        <div>
                            <Label htmlFor="qs-name" className="label-uppercase mb-2 block">
                                {t('supplier_invoices.supplier_name_label')}
                            </Label>
                            <Input
                                id="qs-name"
                                autoFocus
                                value={quickSupplier?.name || ''}
                                onChange={(e) => setQuickSupplier((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder={t('supplier_invoices.supplier_name_placeholder')}
                                data-testid="quick-supplier-name"
                            />
                        </div>
                        <div>
                            <Label htmlFor="qs-org" className="label-uppercase mb-2 block">
                                {t('supplier_invoices.org_number_label')}
                            </Label>
                            <Input
                                id="qs-org"
                                value={quickSupplier?.org_number || ''}
                                onChange={(e) => setQuickSupplier((prev) => ({ ...prev, org_number: e.target.value }))}
                                placeholder={t('supplier_invoices.org_number_placeholder')}
                                data-testid="quick-supplier-org"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setQuickSupplier(null)} disabled={creatingSupplier}>
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={creatingSupplier} data-testid="quick-supplier-save">
                                {creatingSupplier && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {t('common.save')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Manage Suppliers Dialog */}
            <Dialog open={manageSuppliersOpen} onOpenChange={handleManageSuppliersOpenChange}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            {t('supplier_invoices.manage_suppliers_title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('supplier_invoices.manage_suppliers_description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
                        {/* Add / Edit form */}
                        <div className="border border-border p-4 space-y-3">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                                {editingSupplierId
                                    ? t('supplier_invoices.edit_supplier')
                                    : t('supplier_invoices.add_supplier')}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-uppercase mb-2 block">
                                        {t('supplier_invoices.supplier_name_label')}
                                    </Label>
                                    <Input
                                        value={supplierForm.name}
                                        onChange={(e) =>
                                            setSupplierForm((prev) => ({ ...prev, name: e.target.value }))
                                        }
                                        placeholder={t('supplier_invoices.supplier_name_placeholder')}
                                        data-testid="supplier-name-input"
                                    />
                                </div>
                                <div>
                                    <Label className="label-uppercase mb-2 block">
                                        {t('supplier_invoices.org_number_label')}
                                    </Label>
                                    <Input
                                        value={supplierForm.org_number}
                                        onChange={(e) =>
                                            setSupplierForm((prev) => ({ ...prev, org_number: e.target.value }))
                                        }
                                        placeholder={t('supplier_invoices.org_number_placeholder')}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="label-uppercase mb-2 block">
                                    {t('supplier_invoices.default_project_label')}
                                </Label>
                                <Select
                                    value={supplierForm.default_project_id || SELECT_NONE}
                                    onValueChange={(v) =>
                                        setSupplierForm((prev) => ({
                                            ...prev,
                                            default_project_id: v === SELECT_NONE ? '' : v,
                                        }))
                                    }
                                >
                                    <SelectTrigger data-testid="supplier-default-project-select">
                                        <SelectValue
                                            placeholder={t('supplier_invoices.default_project_placeholder')}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* Without this an admin could set a default project but never
                                            clear it again. handleSaveSupplier maps '' to null. */}
                                        <SelectItem value={SELECT_NONE}>
                                            {t('supplier_invoices.default_project_none')}
                                        </SelectItem>
                                        {selectableProjectsForSupplierDefault.map((p) => (
                                            <SelectItem key={p.id} value={p.id} disabled={!p.cost_code_id}>
                                                {p.project_code ? `${p.project_code} — ${p.name}` : p.name}
                                                {!p.cost_code_id
                                                    ? ` (${t('supplier_invoices.project_needs_cost_center')})`
                                                    : ''}
                                                {!p.active ? ` (${t('common.inactive')})` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-uppercase mb-2 block">
                                    {t('supplier_invoices.notes_label')}
                                </Label>
                                <Textarea
                                    value={supplierForm.notes}
                                    onChange={(e) =>
                                        setSupplierForm((prev) => ({ ...prev, notes: e.target.value }))
                                    }
                                    rows={2}
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                {editingSupplierId && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={openAddSupplierForm}
                                        disabled={savingSupplier}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    onClick={handleSaveSupplier}
                                    disabled={savingSupplier}
                                    data-testid="save-supplier-btn"
                                >
                                    {savingSupplier
                                        ? t('common.saving')
                                        : editingSupplierId
                                            ? t('supplier_invoices.save_supplier')
                                            : t('supplier_invoices.add_supplier')}
                                </Button>
                            </div>
                        </div>

                        {/* Supplier list */}
                        <div className="border border-border divide-y divide-border">
                            {suppliers.length === 0 ? (
                                <div className="p-6 text-center text-muted-foreground text-sm">
                                    {t('supplier_invoices.no_suppliers')}
                                </div>
                            ) : (
                                suppliers.map((s) => (
                                    <div key={s.id} className="flex items-center justify-between gap-3 p-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium truncate">{s.name}</p>
                                                <Badge
                                                    variant={s.is_active ? 'outline' : 'secondary'}
                                                    className="shrink-0"
                                                >
                                                    {s.is_active ? t('common.active') : t('common.inactive')}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {s.org_number || t('supplier_invoices.no_org_number')}
                                                {s.default_project_id &&
                                                    (() => {
                                                        const p = projects.find(
                                                            (pr) => pr.id === s.default_project_id
                                                        );
                                                        return p ? ` · ${p.name}` : '';
                                                    })()}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditSupplierForm(s)}
                                                aria-label={t('common.edit')}
                                                data-testid={`edit-supplier-${s.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Switch
                                                checked={s.is_active}
                                                onCheckedChange={() => handleToggleSupplierActive(s)}
                                                aria-label={
                                                    s.is_active
                                                        ? t('supplier_invoices.deactivate_supplier')
                                                        : t('supplier_invoices.activate_supplier')
                                                }
                                                data-testid={`toggle-supplier-active-${s.id}`}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setManageSuppliersOpen(false)}>
                            {t('common.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
